import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, PlugZap, Radio, HeartPulse } from 'lucide-react';

/* ─── Utilities ──────────────────────────────────────────────────────── */
const cn = (...classes: (string | boolean | undefined)[]) =>
  classes.filter(Boolean).join(' ');

function useInView(ref: React.RefObject<HTMLElement | null>, once = true) {
  const [inView, setInView] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || (once && triggered.current)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          setInView(true);
          if (once) {
            triggered.current = true;
            observer.disconnect();
          }
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, once]);

  return inView;
}

/* ─── Syntax Highlighting ────────────────────────────────────────────── */
type TokenType =
  | 'command'
  | 'flag'
  | 'string'
  | 'number'
  | 'operator'
  | 'path'
  | 'variable'
  | 'comment'
  | 'default';

interface Token {
  type: TokenType;
  value: string;
}

function tokenizeBash(text: string): Token[] {
  const tokens: Token[] = [];
  const words = text.split(/(\s+)/);
  let isFirstWord = true;

  for (const word of words) {
    if (/^\s+$/.test(word)) {
      tokens.push({ type: 'default', value: word });
      continue;
    }
    if (word.startsWith('#')) {
      tokens.push({ type: 'comment', value: word });
      continue;
    }
    if (word.startsWith('$')) {
      tokens.push({ type: 'variable', value: word });
      isFirstWord = false;
      continue;
    }
    if (word.startsWith('--') || (word.startsWith('-') && word.length > 1 && !word.startsWith('-d') && !word.startsWith('-X'))) {
      tokens.push({ type: 'flag', value: word });
      isFirstWord = false;
      continue;
    }
    if (/^["'].*["']$/.test(word) || word.startsWith("'{") || word.endsWith("}'")) {
      tokens.push({ type: 'string', value: word });
      isFirstWord = false;
      continue;
    }
    if (/^\d+$/.test(word)) {
      tokens.push({ type: 'number', value: word });
      isFirstWord = false;
      continue;
    }
    if (/^[|>&<\\]+$/.test(word)) {
      tokens.push({ type: 'operator', value: word });
      isFirstWord = true;
      continue;
    }
    if (word.includes('/') || word.startsWith('.') || word.startsWith('~') || word.startsWith('https:')) {
      tokens.push({ type: 'path', value: word });
      isFirstWord = false;
      continue;
    }
    if (isFirstWord) {
      tokens.push({ type: 'command', value: word });
      isFirstWord = false;
      continue;
    }
    tokens.push({ type: 'default', value: word });
  }

  return tokens;
}

const tokenColors: Record<TokenType, string> = {
  command: 'text-emerald-400',
  flag: 'text-sky-400',
  string: 'text-amber-300',
  number: 'text-purple-400',
  operator: 'text-red-400',
  path: 'text-cyan-300',
  variable: 'text-pink-400',
  comment: 'text-neutral-500',
  default: 'text-neutral-300',
};

function SyntaxHighlightedText({ text }: { text: string }) {
  const tokens = tokenizeBash(text);
  return (
    <>
      {tokens.map((token, i) => (
        <span key={i} className={tokenColors[token.type]}>
          {token.value}
        </span>
      ))}
    </>
  );
}

/* ─── Terminal Component ─────────────────────────────────────────────── */
interface TerminalLine {
  type: 'command' | 'output';
  content: string;
}

interface TerminalProps {
  commands: string[];
  outputs?: Record<number, string[]>;
  username?: string;
  className?: string;
  typingSpeed?: number;
  delayBetweenCommands?: number;
  initialDelay?: number;
  resetKey?: number;
}

function Terminal({
  commands = ['echo "Hello"'],
  outputs = {},
  username = 'admin',
  className,
  typingSpeed = 45,
  delayBetweenCommands = 800,
  initialDelay = 400,
  resetKey = 0,
}: TerminalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef);

  const [lines, setLines] = useState<TerminalLine[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [commandIdx, setCommandIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [outputIdx, setOutputIdx] = useState(-1);
  const [phase, setPhase] = useState<
    'idle' | 'typing' | 'executing' | 'outputting' | 'pausing' | 'done'
  >('idle');
  const [cursorVisible, setCursorVisible] = useState(true);

  // Reset on tab change
  useEffect(() => {
    setLines([]);
    setCurrentText('');
    setCommandIdx(0);
    setCharIdx(0);
    setOutputIdx(-1);
    setPhase('idle');
  }, [resetKey, commands]);

  const currentCommand = commands[commandIdx] || '';
  const currentOutputs = useMemo(
    () => outputs[commandIdx] || [],
    [outputs, commandIdx],
  );
  const isLastCommand = commandIdx === commands.length - 1;

  // Start on view
  useEffect(() => {
    if (!inView || phase !== 'idle') return;
    const t = setTimeout(() => setPhase('typing'), initialDelay);
    return () => clearTimeout(t);
  }, [inView, phase, initialDelay]);

  // Typing phase
  useEffect(() => {
    if (phase !== 'typing') return;

    if (charIdx < currentCommand.length) {
      const t = setTimeout(
        () => {
          setCurrentText(currentCommand.slice(0, charIdx + 1));
          setCharIdx((c) => c + 1);
        },
        typingSpeed + Math.random() * 30,
      );
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setPhase('executing'), 120);
      return () => clearTimeout(t);
    }
  }, [phase, charIdx, currentCommand, typingSpeed]);

  // Execute phase
  useEffect(() => {
    if (phase !== 'executing') return;

    setLines((prev) => [...prev, { type: 'command', content: currentCommand }]);
    setCurrentText('');

    if (currentOutputs.length > 0) {
      setOutputIdx(0);
      setPhase('outputting');
    } else if (isLastCommand) {
      setPhase('done');
    } else {
      setPhase('pausing');
    }
  }, [phase, currentCommand, currentOutputs.length, isLastCommand]);

  // Output phase
  useEffect(() => {
    if (phase !== 'outputting') return;

    if (outputIdx >= 0 && outputIdx < currentOutputs.length) {
      const t = setTimeout(() => {
        setLines((prev) => [
          ...prev,
          { type: 'output', content: currentOutputs[outputIdx] },
        ]);
        setOutputIdx((i) => i + 1);
      }, 120);
      return () => clearTimeout(t);
    } else if (outputIdx >= currentOutputs.length) {
      const t = setTimeout(() => {
        if (isLastCommand) {
          setPhase('done');
        } else {
          setPhase('pausing');
        }
      }, 300);
      return () => clearTimeout(t);
    }
  }, [phase, outputIdx, currentOutputs, isLastCommand]);

  // Pause between commands
  useEffect(() => {
    if (phase !== 'pausing') return;
    const t = setTimeout(() => {
      setCharIdx(0);
      setOutputIdx(-1);
      setCommandIdx((c) => c + 1);
      setPhase('typing');
    }, delayBetweenCommands);
    return () => clearTimeout(t);
  }, [phase, delayBetweenCommands]);

  // Cursor blink
  useEffect(() => {
    const interval = setInterval(() => setCursorVisible((v) => !v), 530);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [lines, phase]);

  const prompt = (
    <span className="text-neutral-500">
      <span className="text-sky-500">{username}</span>
      <span className="text-emerald-600">:</span>
      <span className="text-sky-400">~</span>
      <span className="text-neutral-500">$</span>{' '}
    </span>
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative z-20 mx-auto h-full min-h-80 w-full font-mono text-xs',
        className,
      )}
    >
      <div className="h-full overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950 shadow-2xl">
        {/* Window Title Bar */}
        <div className="flex items-center gap-2 bg-neutral-900 px-4 py-3 border-b border-neutral-800">
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded-full bg-red-500/80"></div>
            <div className="h-3 w-3 rounded-full bg-yellow-500/80"></div>
            <div className="h-3 w-3 rounded-full bg-green-500/80"></div>
          </div>
          <div className="flex-1 text-center">
            <span className="truncate text-xs text-neutral-500 font-medium">
              hospital-admin — bash
            </span>
          </div>
          <div className="w-[52px]"></div>
        </div>

        {/* Terminal Content */}
        <div
          ref={contentRef}
          className="h-full overflow-y-auto p-4 font-mono scrollbar-none"
          style={{ scrollbarWidth: 'none' }}
        >
          {lines.map((line, i) => (
            <div key={i} className="leading-relaxed whitespace-pre-wrap">
              {line.type === 'command' ? (
                <span>
                  {prompt}
                  <SyntaxHighlightedText text={line.content} />
                </span>
              ) : (
                <span className="text-neutral-400">{line.content}</span>
              )}
            </div>
          ))}

          {phase === 'typing' && (
            <div className="leading-relaxed whitespace-pre-wrap">
              {prompt}
              <SyntaxHighlightedText text={currentText} />
              <span className="ml-0.5 inline-block h-4 w-2 bg-neutral-300 align-middle"></span>
            </div>
          )}

          {(phase === 'done' || phase === 'pausing' || phase === 'outputting') && (
            <div className="leading-relaxed whitespace-pre-wrap">
              {prompt}
              <span
                className={cn(
                  'inline-block h-4 w-2 bg-neutral-300 align-middle transition-opacity duration-100',
                  !cursorVisible && 'opacity-0',
                )}
              ></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Hospital Status Developer Features ─────────────────────────────── */
interface Feature {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  commands: string[];
  outputs: Record<number, string[]>;
}

const devFeatures: Feature[] = [
  {
    id: 'setup',
    title: 'Activación rápida',
    description:
      'Registra tu hospital, define quirófanos y obtén tu API Key. Zero infraestructura propia requerida.',
    icon: <Download className="w-5 h-5" />,
    commands: [
      'curl -X POST https://api.hospital-status.com/v1/register -H "Content-Type: application/json" -d \'{"hospital":"Hospital Ejemplo","rooms":6}\'',
    ],
    outputs: {
      0: [
        '',
        '✔ Hospital registrado exitosamente.',
        '✔ 6 quirófanos configurados (OR-1 a OR-6).',
        '✔ API Key generada: hs_live_k8x92m4n...',
        '✔ Dashboard listo en: hospital-status.com/ejemplo/admin',
        '',
        '→ Siguiente paso: conecta tu ERP.',
      ],
    },
  },
  {
    id: 'erp',
    title: 'Integración con tu ERP',
    description:
      'Webhooks nativos para Tasy, SAP y sistemas hospitalarios. Cada cambio en tu ERP se refleja en pantallas y celulares al instante.',
    icon: <PlugZap className="w-5 h-5" />,
    commands: [
      'hospital-status connect --erp tasy --webhook https://ejemplo.hospital.mx/hs-hook --events room.update,patient.admit',
    ],
    outputs: {
      0: [
        '',
        '✔ Conectando con Tasy ERP...',
        '✔ Webhook registrado: https://ejemplo.hospital.mx/hs-hook',
        '✔ Eventos suscritos: room.update, patient.admit',
        '✔ Test ping enviado → 200 OK (12ms)',
        '',
        '✔ Integración activa.',
        '  Los cambios en Tasy se reflejan en pantallas en <2 segundos.',
      ],
    },
  },
  {
    id: 'realtime',
    title: 'Actualización en tiempo real',
    description:
      'Un solo endpoint actualiza pantallas en sala, notifica familias y registra analytics. Latencia sub-segundo.',
    icon: <Radio className="w-5 h-5" />,
    commands: [
      'curl -X PATCH https://api.hospital-status.com/v1/rooms/OR-1 -H "Authorization: Bearer hs_live_k8x92m4n..." -d \'{"status":"surgery","code":"MT001"}\'',
    ],
    outputs: {
      0: [
        '',
        '✔ Quirófano OR-1 actualizado.',
        '',
        '  Status:     EN CIRUGÍA',
        '  Código:     MT001',
        '  Pantalla:   actualizada ✓',
        '  Familias:   3 dispositivos notificados ✓',
        '  Analytics:  evento registrado ✓',
      ],
    },
  },
  {
    id: 'health',
    title: 'Infraestructura global 24/7',
    description:
      'Infraestructura edge desplegada globalmente. 99.98% uptime. Monitoreo activo de cada servicio en tiempo real.',
    icon: <HeartPulse className="w-5 h-5" />,
    commands: ['hospital-status health --hospital ejemplo'],
    outputs: {
      0: [
        '',
        '┌──────────────────┬──────────┬───────────────┐',
        '│ Servicio         │ Estado   │ Latencia      │',
        '├──────────────────┼──────────┼───────────────┤',
        '│ API Gateway      │ ✔ Online │ 12ms          │',
        '│ Base de Datos    │ ✔ Online │ 3ms           │',
        '│ SSE (Realtime)   │ ✔ Online │ <1ms          │',
        '│ Pantallas TV     │ ✔ Online │ 2 conectadas  │',
        '│ Uptime (30d)     │ 99.98%   │               │',
        '└──────────────────┴──────────┴───────────────┘',
      ],
    },
  },
];

/* ─── Feature Card ───────────────────────────────────────────────────── */
function DevFeatureCard({
  feature,
  isActive,
  onClick,
}: {
  feature: Feature;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <motion.div
      onClick={onClick}
      className={cn(
        'cursor-pointer rounded-xl transition-all duration-200',
        isActive && 'bg-white shadow-[0_4px_20px_rgb(0,0,0,0.06)] ring-1 ring-slate-200/60',
      )}
      layout
    >
      <div className="flex items-center gap-4 p-4">
        <div
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300',
            isActive
              ? 'bg-med-accent/10 text-med-accent'
              : 'bg-slate-100 text-slate-400',
          )}
        >
          {feature.icon}
        </div>
        <div className="flex-1">
          <h3
            className={cn(
              'font-medium text-sm font-heading tracking-[-0.015em] transition-colors',
              isActive ? 'text-med-navy' : 'text-slate-500',
            )}
          >
            {feature.title}
          </h3>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isActive && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <p className="text-sm text-med-navy/50 leading-[1.6] font-normal">
                {feature.description}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ─── Exported Section ───────────────────────────────────────────────── */
export function FeatureTerminal() {
  const [activeFeature, setActiveFeature] = useState<string>('setup');
  const [resetKey, setResetKey] = useState(0);

  const currentFeature = devFeatures.find((f) => f.id === activeFeature)!;

  const handleFeatureClick = (featureId: string) => {
    if (featureId !== activeFeature) {
      setActiveFeature(featureId);
      setResetKey((k) => k + 1);
    }
  };

  return (
    <section id="integracion" className="w-full bg-white py-16 md:py-28 relative overflow-hidden scroll-mt-24">
      {/* Decorative Background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-med-accent/5 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="relative mx-auto w-full max-w-6xl px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 border border-slate-200 mb-6">
            <PlugZap className="w-3.5 h-3.5 text-med-accent" />
            <span className="text-[11px] font-medium text-slate-600 uppercase tracking-[0.08em] font-heading">
              Integración & API
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold tracking-[-0.025em] text-med-navy font-heading mb-5 leading-[1.15]">
            Compatible con{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-med-accent">
              tu infraestructura actual.
            </span>
          </h2>
          <p className="max-w-lg text-lg text-med-navy/50 font-normal leading-[1.6]">
            API REST documentada. Webhooks nativos para Tasy, SAP y más. Sin servidores que administrar.
            Tu equipo de TI lo activa en una tarde.
          </p>
        </motion.div>

        {/* Terminal + Feature Cards Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="grid overflow-hidden rounded-3xl bg-med-bg shadow-[0_20px_60px_-15px_rgba(18,32,86,0.1)] ring-1 ring-slate-200/60 lg:grid-cols-2"
        >
          {/* Terminal Side */}
          <div className="relative order-2 flex h-full items-center justify-center p-4 md:p-6 lg:order-1 bg-gradient-to-br from-slate-900 via-slate-950 to-med-navy min-h-[360px]">
            {/* Subtle grid pattern */}
            <div
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            ></div>
            <Terminal
              commands={currentFeature.commands}
              outputs={currentFeature.outputs}
              username="hospital-admin"
              resetKey={resetKey}
              typingSpeed={35}
              delayBetweenCommands={600}
            />
          </div>

          {/* Feature Cards Side */}
          <div className="order-1 space-y-2 p-4 md:p-6 lg:order-2 bg-med-bg">
            {devFeatures.map((feature) => (
              <DevFeatureCard
                key={feature.id}
                feature={feature}
                isActive={activeFeature === feature.id}
                onClick={() => handleFeatureClick(feature.id)}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
